import { RootZoneSparkline } from 'App/dashboards/_components/RootZoneSparkline'
import { DASHBOARD_FONT_FAMILY } from 'App/dashboards/_utils/dashboardFont'
import type { TSoilDeficitZone } from 'App/dashboards/_utils/soilDeficit'
import { soilDeficitZoneColor, soilDeficitZoneLabel } from 'App/dashboards/_utils/soilDeficit'
import { colors, dashboardColors } from 'settings/colors'

// Vertical rhythm, mirroring the dialog: 24 top padding, title row, subtitle, then the chart block and
// legend. The footer is deliberately absent — the export is the content, not the dialog's actions.
const PADDING_X = 24
const TITLE_TOP = 24
const TITLE_HEIGHT = 20
const SUBTITLE_TOP = TITLE_TOP + TITLE_HEIGHT + 6
const SUBTITLE_HEIGHT = 20
const PLOT_TOP = SUBTITLE_TOP + SUBTITLE_HEIGHT + 16
const X_AXIS_TOP = PLOT_TOP + 208 + 6
const LEGEND_TOP = X_AXIS_TOP + 16 + 16
const HEIGHT = LEGEND_TOP + 16 + 16
const Y_AXIS_WIDTH = 32
const AXIS_GAP = 8
const PLOT_HEIGHT = 208
const SWATCH = 10
const LEGEND_GAP = 16
const LEGEND_ITEM_GAP = 6

// SVG text sits on its alphabetic baseline, so centre a line by nudging down from its middle. 0.36em
// is the usual approximation for Roboto's cap height.
const baselineOf = (top: number, lineHeight: number, fontSize: number): number =>
  top + lineHeight / 2 + fontSize * 0.36

// Rough advance width per character, used only to lay the legend out left-to-right. Exact metrics
// aren't available without measuring, and the legend has generous spacing.
const approxTextWidth = (text: string, fontSize: number): number => text.length * fontSize * 0.55

// First label hugs the plot's left edge, last hugs the right, the rest centre on their tick — the same
// rule the in-dialog x-axis follows via flex space-between.
const anchorFor = (index: number, count: number): 'start' | 'end' | 'middle' => {
  if (index === 0) return 'start'

  if (index === count - 1) return 'end'

  return 'middle'
}

/**
 * The dialog's content re-drawn as a single self-contained SVG, purely so it can be rasterised.
 *
 * The pest chart gets this for free — Highcharts renders its title, axes and legend into one SVG, so
 * `downloadSvgAsJpg` on that element captures everything. Here only the plot is SVG and the rest is
 * DOM, so an export of the live markup would be the bare chart. Rather than add a DOM rasteriser, this
 * mirrors the visible content into SVG, which is also what DashboardChart does when it swaps its HTML
 * legend for an SVG-native one during a snapshot.
 *
 * Rendered off-screen (not `display: none`, which would collapse the layout the rasteriser measures).
 */
export const SoilChartExport = ({
  svgRef,
  width,
  title,
  reading,
  readingColors,
  subtitle,
  values,
  dates,
  madValue,
  yTicks,
  topOf,
  xTicks,
  zones,
}: {
  svgRef: React.Ref<SVGSVGElement>
  width: number
  title: string
  reading: string | null
  // Deficit-band colours for the reading badge, so the export matches the dialog.
  readingColors: { background: string; color: string } | null
  subtitle: string
  values: (number | null)[]
  dates?: string[]
  madValue?: number
  yTicks: number[]
  topOf: (tick: number) => number
  xTicks: string[]
  zones: TSoilDeficitZone[]
}) => {
  const plotWidth = width - PADDING_X * 2 - Y_AXIS_WIDTH - AXIS_GAP
  const plotLeft = PADDING_X + Y_AXIS_WIDTH + AXIS_GAP
  const titleWidth = approxTextWidth(title, 16)

  let legendX = PADDING_X

  return (
    <svg
      ref={svgRef}
      width={width}
      height={HEIGHT}
      viewBox={`0 0 ${width} ${HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
      css={{ fontFamily: DASHBOARD_FONT_FAMILY }}
    >
      <rect x={0} y={0} width={width} height={HEIGHT} fill={colors.white} />

      <text
        x={PADDING_X}
        y={baselineOf(TITLE_TOP, TITLE_HEIGHT, 16)}
        fontFamily={DASHBOARD_FONT_FAMILY}
        fontSize={16}
        fontWeight={500}
        fill={dashboardColors.foreground}
      >
        {title}
      </text>

      {reading !== null && (
        <>
          <rect
            x={PADDING_X + titleWidth + 8}
            y={TITLE_TOP}
            width={approxTextWidth(reading, 12) + 16}
            height={TITLE_HEIGHT}
            rx={TITLE_HEIGHT / 2}
            fill={readingColors?.background ?? dashboardColors.primary}
          />
          <text
            x={PADDING_X + titleWidth + 8 + (approxTextWidth(reading, 12) + 16) / 2}
            y={baselineOf(TITLE_TOP, TITLE_HEIGHT, 12)}
            textAnchor="middle"
            fontFamily={DASHBOARD_FONT_FAMILY}
            fontSize={12}
            fontWeight={500}
            fill={readingColors?.color ?? dashboardColors.primaryForeground}
          >
            {reading}
          </text>
        </>
      )}

      <text
        x={PADDING_X}
        y={baselineOf(SUBTITLE_TOP, SUBTITLE_HEIGHT, 14)}
        fontFamily={DASHBOARD_FONT_FAMILY}
        fontSize={14}
        fill={dashboardColors.mutedText}
      >
        {subtitle}
      </text>

      {yTicks.map((tick) => (
        <text
          key={tick}
          x={PADDING_X + Y_AXIS_WIDTH}
          y={baselineOf(PLOT_TOP + topOf(tick), 16, 12)}
          textAnchor="end"
          fontFamily={DASHBOARD_FONT_FAMILY}
          fontSize={12}
          fill={dashboardColors.mutedText}
        >
          {`${tick}%`}
        </text>
      ))}

      {/* The live plot, nested so the bands and line come from the same component the dialog shows. */}
      <g transform={`translate(${plotLeft}, ${PLOT_TOP})`}>
        <RootZoneSparkline
          values={values}
          dates={dates}
          madValue={madValue}
          width={plotWidth}
          height={PLOT_HEIGHT}
        />
      </g>

      {xTicks.map((label, index) => (
        <text
          key={`${label}-${index}`}
          x={plotLeft + (index / Math.max(xTicks.length - 1, 1)) * plotWidth}
          y={baselineOf(X_AXIS_TOP, 16, 12)}
          textAnchor={anchorFor(index, xTicks.length)}
          fontFamily={DASHBOARD_FONT_FAMILY}
          fontSize={12}
          fill={dashboardColors.mutedText}
        >
          {label}
        </text>
      ))}

      {zones.map((zone) => {
        const label = soilDeficitZoneLabel(zone)
        const x = legendX

        legendX += SWATCH + LEGEND_ITEM_GAP + approxTextWidth(label, 12) + LEGEND_GAP

        return (
          <g key={zone}>
            <rect
              x={x}
              y={LEGEND_TOP + (16 - SWATCH) / 2}
              width={SWATCH}
              height={SWATCH}
              rx={2}
              fill={soilDeficitZoneColor[zone]}
            />
            <text
              x={x + SWATCH + LEGEND_ITEM_GAP}
              y={baselineOf(LEGEND_TOP, 16, 12)}
              fontFamily={DASHBOARD_FONT_FAMILY}
              fontSize={12}
              fill={dashboardColors.foreground}
            >
              {label}
            </text>
          </g>
        )
      })}
    </svg>
  )
}
